import { useEffect, useState } from "react";
import { Loader2, ScrollText } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ACTION_VARIANT = (action) => {
  if (action.endsWith(".create") || action.endsWith(".launch")) return "default";
  if (action.endsWith(".delete") || action.endsWith(".cancel") || action.endsWith(".revoke")) return "destructive";
  if (action.endsWith(".update") || action.endsWith(".pause") || action.endsWith(".resume")) return "secondary";
  return "outline";
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/admin/audit", { params: { limit: 200 } });
        if (!cancel) setRows(r.data);
      } catch (err) {
        if (!cancel) toastError(err, "Failed to load audit log");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Append-only record of every mutating admin action. Last 200 entries shown,
          most recent first.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="audit-empty">
            <div className="rounded-full bg-secondary p-3">
              <ScrollText className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-base font-medium">No audit entries yet</div>
            <p className="max-w-sm text-sm text-muted-foreground">
              Make a change anywhere in the admin (settings, agents, campaigns, …)
              and it will show up here.
            </p>
          </div>
        ) : (
          <Table data-testid="audit-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">When</TableHead>
                <TableHead className="w-28">Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(r.ts).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{r.actor}</TableCell>
                  <TableCell>
                    <Badge variant={ACTION_VARIANT(r.action)}>{r.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="text-muted-foreground">{r.target_type}</span>
                    {r.target_id && (
                      <span className="ml-2 font-mono text-xs">{r.target_id.slice(0, 8)}…</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all max-w-md">
                      {JSON.stringify(r.details || {}, null, 0)}
                    </pre>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
